import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Turnstile } from '@marsidev/react-turnstile';
import '../css/compatibility-checker.css';

const EMPTY_FORM = { firstName: '', lastName: '', email: '', phone: '', message: '' };

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhone(phone) {
  return /^[\d\s+\-()]{8,}$/.test(phone.trim());
}

/**
 * Simple single-step "Get a Quote" modal — replaces the multi-step
 * compatibility checker / deposit booking flow for the water filter page.
 * Captures name/email/phone, emails the team via the existing
 * /api/contacts endpoint, and lets Mitch call the customer back.
 */
const GetQuoteForm = ({ isOpen, onClose, productId, productLabel }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Reset the form whenever it's (re)opened, so switching products starts fresh.
  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_FORM);
      setErrors({});
      setTurnstileToken('');
      setSubmitting(false);
      setApiError('');
      setSubmitted(false);
    }
  }, [isOpen, productId]);

  if (!isOpen) return null;

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'Required';
    if (!form.lastName.trim()) errs.lastName = 'Required';
    if (!isValidEmail(form.email)) errs.email = 'Enter a valid email';
    if (!isValidPhone(form.phone)) errs.phone = 'Enter a valid phone number';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setApiError('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      await axios.post(`${apiBase}/api/contacts`, {
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone,
        services: `Water Filter Installation – ${productLabel}`,
        message: form.message || `Quote request for ${productLabel}.`,
        turnstileToken,
      });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'quote_form_submit',
        product_id: productId,
        product_name: productLabel,
      });

      setSubmitted(true);
    } catch (err) {
      setApiError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const handleClose = () => onClose();
  const handleOverlayClick = e => { if (e.target === e.currentTarget) handleClose(); };

  return (
    <div className="cc-overlay" onClick={handleOverlayClick}>
      <div className="cc-modal">
        <div className="cc-topbar">
          <span />
          <button className="cc-close" onClick={handleClose} aria-label="Close">✕</button>
        </div>

        {!submitted ? (
          <>
            <div className="cc-step">
              <div className="cc-section">
                <p className="cc-section-label">Get A Free Quote</p>
                <h2 className="cc-title">{productLabel}</h2>
                <p className="cc-subtitle">Pop in your details and Mitch will call you back to talk through your quote.</p>

                <div className="cc-field-row">
                  <div className="cc-field">
                    <label className="cc-field-label">First Name</label>
                    <input
                      type="text"
                      className={`cc-input ${errors.firstName ? 'cc-input--error' : ''}`}
                      placeholder="Jane"
                      value={form.firstName}
                      onChange={e => setField('firstName', e.target.value)}
                    />
                    {errors.firstName && <span className="cc-field-error">{errors.firstName}</span>}
                  </div>
                  <div className="cc-field">
                    <label className="cc-field-label">Last Name</label>
                    <input
                      type="text"
                      className={`cc-input ${errors.lastName ? 'cc-input--error' : ''}`}
                      placeholder="Smith"
                      value={form.lastName}
                      onChange={e => setField('lastName', e.target.value)}
                    />
                    {errors.lastName && <span className="cc-field-error">{errors.lastName}</span>}
                  </div>
                </div>

                <div className="cc-field">
                  <label className="cc-field-label">Email Address</label>
                  <input
                    type="email"
                    className={`cc-input ${errors.email ? 'cc-input--error' : ''}`}
                    placeholder="jane@example.com"
                    value={form.email}
                    onChange={e => setField('email', e.target.value)}
                  />
                  {errors.email && <span className="cc-field-error">{errors.email}</span>}
                </div>

                <div className="cc-field">
                  <label className="cc-field-label">Phone Number</label>
                  <input
                    type="tel"
                    inputMode="tel"
                    className={`cc-input ${errors.phone ? 'cc-input--error' : ''}`}
                    placeholder="04xx xxx xxx"
                    value={form.phone}
                    onChange={e => setField('phone', e.target.value)}
                  />
                  {errors.phone && <span className="cc-field-error">{errors.phone}</span>}
                </div>

                <div className="cc-field">
                  <label className="cc-field-label">
                    Anything else? <span className="cc-optional">— Optional</span>
                  </label>
                  <textarea
                    className="cc-input"
                    rows={3}
                    placeholder="e.g. number of bathrooms, best time to call…"
                    value={form.message}
                    onChange={e => setField('message', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="cc-footer">
              {apiError && <p className="cc-api-error">{apiError}</p>}
              <Turnstile
                siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                onSuccess={setTurnstileToken}
                onError={() => setTurnstileToken('')}
                onExpire={() => setTurnstileToken('')}
                options={{ theme: 'light', size: 'normal', appearance: 'always' }}
                style={{ marginBottom: 12, width: '100%' }}
              />
              <button
                className="cc-next-btn"
                onClick={handleSubmit}
                disabled={submitting || !turnstileToken}
              >
                {submitting ? (
                  <span className="cc-spinner-wrap">
                    <span className="cc-spinner" />
                    Sending…
                  </span>
                ) : (
                  'Get My Free Quote →'
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="cc-booked">
            <div className="cc-booked-icon">✓</div>
            <h2 className="cc-booked-title">Thanks, {form.firstName}!</h2>
            <p className="cc-booked-desc">
              We've received your quote request for the <strong>{productLabel}</strong>.
              Mitch will give you a call shortly to talk through pricing and book your install.
            </p>
            <div className="cc-booked-steps">
              <div className="cc-booked-step"><span>📧</span><span>Confirmation sent to {form.email}</span></div>
              <div className="cc-booked-step"><span>📞</span><span>Mitch calls to discuss your quote</span></div>
              <div className="cc-booked-step"><span>📅</span><span>Installation booked once you're ready</span></div>
            </div>
            <button className="cc-booked-close" onClick={handleClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GetQuoteForm;
